import styled from 'styled-components';
import { CircularProgress, Tooltip } from '@mui/material';
import useUrlFilters from '../../../hooks/useUrlFilters';
import Button from '../../Button';
import { QuickFilter } from '../types';
import { space } from '../../../style/utils';

const StyledButton = styled(Button)<{ $isActive: boolean }>`
  gap: ${space('xs')};

  ${({ $isActive, theme }) => {
    if (!$isActive) return '';
    const { border, bg, fg } = theme.tokens.components.button.quickFilterActive;
    return `
      border: 1px solid ${border};
      ${bg ? `background: ${bg};` : ''}
      ${fg ? `color: ${fg};` : ''}
    `;
  }}
`;

type Props = {
  urlParamsPrefix: string;
  quickFilters: QuickFilter[];
  clearFiltersChipLabel?: string;
  isClearFiltersLoading?: boolean;
  disabled?: boolean;
};

const QuickFilters = ({
  urlParamsPrefix,
  quickFilters,
  clearFiltersChipLabel,
  isClearFiltersLoading = false,
  disabled,
}: Props) => {
  const { urlFilters, clearAllFilters, applyFilters } = useUrlFilters({
    urlKey: urlParamsPrefix,
  });

  const isFilterActive = (quickFilter: QuickFilter) =>
    JSON.stringify(quickFilter.filters).toLowerCase() ===
    JSON.stringify(urlFilters || []).toLowerCase();

  return (
    <>
      {clearFiltersChipLabel && (
        <StyledButton
          key="clear-filters-chip"
          color="secondary"
          variant="outlined"
          $isActive={urlFilters === undefined}
          onClick={() => clearAllFilters()}
          disabled={disabled}
        >
          {clearFiltersChipLabel}
          {isClearFiltersLoading && (
            <CircularProgress size={16} sx={{ ml: 0.5 }} />
          )}
        </StyledButton>
      )}

      {quickFilters?.map((filter) => (
        <Tooltip key={filter.name} title={filter.tooltip}>
          {/* Wrapper span to enable tooltip on disabled button */}
          <span>
            <StyledButton
              key={filter.name}
              color="secondary"
              variant="outlined"
              $isActive={isFilterActive(filter)}
              disabled={disabled}
              onClick={() =>
                isFilterActive(filter)
                  ? clearAllFilters()
                  : applyFilters(filter.filters)
              }
            >
              {filter.icon}
              {filter.name}
              {filter.loading && (
                <CircularProgress size={16} sx={{ ml: 0.5 }} />
              )}
            </StyledButton>
          </span>
        </Tooltip>
      ))}
    </>
  );
};

export default QuickFilters;
